import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/da869o1tacug2b80sd60';

export default function ContactPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = `${t('footerContact')} | CSSBuy Spreadsheet`;
  }, [t]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // Anti-spam honeypot: if filled, silently pretend success without submitting.
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string') payload.append(key, value);
    });

    try {
      const res = await fetch(CONTACT_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      const isSuccess = res.ok && parsed.code === 'OK';

      if (isSuccess) {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(typeof msg === 'string' && msg.trim() ? msg : t('error'));
      }
    } catch {
      setFormStatus('error');
      setFormError(t('error'));
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        <div className="max-w-6xl mx-auto px-4 md:px-6 pt-24 pb-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors">{t('breadcrumbHome')}</Link>
            <i className="ri-arrow-right-s-line" style={{ width: 16, height: 16 }} />
            <span className="text-foreground-700">{t('footerContact')}</span>
          </nav>
        </div>

        <section className="py-8 md:py-12">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 mb-6">
              {t('footerContact')}
            </h1>
            <p className="text-lg text-foreground-700 leading-relaxed mb-8">
              Tem dúvidas, sugestões ou precisa de ajuda? Entre em contato conosco através do formulário abaixo. Responderemos o mais breve possível.
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl text-foreground-950 mb-4">Informações</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className="ri-mail-line text-primary-600" style={{ width: 20, height: 20 }} />
                    </div>
                    <div>
                      <p className="font-medium text-foreground-950 text-sm">Email</p>
                      <p className="text-sm text-foreground-600">contact@cssbuybr.lol</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                      <i className="ri-time-line text-primary-600" style={{ width: 20, height: 20 }} />
                    </div>
                    <div>
                      <p className="font-medium text-foreground-950 text-sm">Horário</p>
                      <p className="text-sm text-foreground-600">Segunda a Sexta, 9h às 18h</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-xl text-foreground-950 mb-4">Enviar Mensagem</h2>
                <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">Nome</label>
                    <input id="contact-name" type="text" name="name" className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-400" placeholder="Seu nome" required />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">Email</label>
                    <input id="contact-email" type="email" name="email" className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-400" placeholder="seu@email.com" required />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">Mensagem</label>
                    <textarea id="contact-message" name="message" className="w-full px-4 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-950 focus:outline-none focus:border-primary-400 resize-none" rows={4} placeholder="Sua mensagem..." maxLength={500} required />
                  </div>
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
                  <button type="submit" disabled={formStatus === 'submitting'} className="w-full px-6 py-3 text-sm font-medium bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed">
                    {formStatus === 'submitting' ? t('loading') : 'Enviar Mensagem'}
                  </button>
                </form>
                {formStatus === 'success' && (
                  <p className="mt-4 text-sm text-accent-600">Mensagem enviada com sucesso! Obrigado pelo contato.</p>
                )}
                {formStatus === 'error' && (
                  <p className="mt-4 text-sm text-primary-600">{formError}</p>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}